import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  addMinutes,
  ConfirmResult,
  DirectionEnum,
  ICourseDto,
  IRideMetadata,
  IStationInfo,
  isValidTimeString,
  MIDNIGHT_TIME,
  timeDifferenceSameDate,
  useTimezone,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { useModalConfirm } from "../../hooks-singletons";
import { useRidesNavigation } from "../../navigation";
import { useNewRideTemplate } from "./use-new-ride-template";
import { useRideEdit } from "./use-ride-edit";

export const useRideDebugActions = () => {
  const { confirmDialogResult } = useModalConfirm();
  const { navigateRidesEdit } = useRidesNavigation();
  const { createRideBySnapshot } = useRideEdit();
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { getNewRideTemplate } = useNewRideTemplate();

  const createRideByCourse = useEvent(
    async (courseDto: ICourseDto): Promise<void> => {
      if (!courseDto?.id) {
        return;
      }

      const confirmationResult = await confirmDialogResult({
        title: t("general.CONFIRM"),
        message: t("course.CREATE_RIDE_WITH_COURSE"),
      });

      if (confirmationResult !== ConfirmResult.OK) {
        return;
      }

      const courseStations = courseDto?.metadata?.stationsInfo || [];

      if (!courseStations.length) {
        return;
      }

      const rideStations: IStationInfo[] = [];
      const baseDate = new Date().toISOString();
      const firstStationTime = courseStations[0]?.timeArrival || MIDNIGHT_TIME;

      if (!isValidTimeString(firstStationTime)) {
        return;
      }

      for (const courseStation of courseStations) {
        const stationTime = courseStation.timeArrival || MIDNIGHT_TIME;

        if (!isValidTimeString(stationTime)) {
          continue;
        }

        const offset = timeDifferenceSameDate(
          firstStationTime,
          stationTime,
          "minute",
          timezone,
        );

        if (offset === undefined) {
          continue;
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { timeArrival, ...stationWithoutTimeArrival } = courseStation;
        rideStations.push({
          ...stationWithoutTimeArrival,
          arrivalTime: addMinutes(baseDate, offset),
        });
      }

      const metadata: IRideMetadata = {
        stationIds: courseDto?.metadata?.stationIds,
        stationsInfo: rideStations,
      };

      const initialData = await getNewRideTemplate({
        metadata,
        rideDate: baseDate,
        direction: DirectionEnum.PickUp,
        courseId: courseDto?.id,
        courseRouteType: courseDto.routeType,
      });
      const rideCreatedResult = await createRideBySnapshot(initialData);

      if (!rideCreatedResult.success) {
        warnAndToastSimpleResult(rideCreatedResult, t);
        return;
      }

      const rideCreated = rideCreatedResult.data;

      if (rideCreated?.id) {
        navigateRidesEdit({ id: rideCreated.id, keepQuery: false });
      }
    },
  );

  return { createRideByCourse };
};
